import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import Typography from '../../../components/Reusables/Typography';
import { GTFonts, useTheme } from '../../../theme';
import { borderWidth, fs, s, v } from '../../../utils/libs';

/**
 * Imperative API exposed by {@link AlphaRail}. The rail's active letter is
 * driven by scroll state held by the parent list, but we never want those
 * high-frequency scroll events to rerender the parent tree. Instead the
 * parent holds a ref to this handle and writes to it directly.
 */
export interface AlphaRailHandle {
  /**
   * Called from onViewableItemsChanged with the letter that currently
   * dominates the viewport. rAF-throttled internally, and ignored while
   * {@link lock} is active.
   */
  setDominant: (letter: string) => void;
  /**
   * Pin the active letter (used while a tap-triggered scroll is animating
   * toward its target). Blocks setDominant until {@link unlock} is called.
   */
  lock: (letter: string) => void;
  /** Release the lock established by {@link lock}. */
  unlock: () => void;
}

interface Props {
  letters: string[];
  onLetterPress: (letter: string) => void;
}

/**
 * A–Z side rail. Owns its own `active` letter state so that scroll-driven
 * updates from the parent list do not rerender the list. Intentionally
 * self-contained (theme + styles) — this is the single source of truth for
 * the feature's visual presentation.
 */
export const AlphaRail = forwardRef<AlphaRailHandle, Props>(
  ({ letters, onLetterPress }, ref) => {
    const { theme } = useTheme();
    const styles = useMemo(() => createStyles(theme), [theme]);

    const [active, setActive] = useState('');
    const lockedRef = useRef<string | null>(null);
    const rafRef = useRef<number | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        setDominant: (l: string) => {
          if (!l) return;
          if (lockedRef.current !== null) return;
          // Coalesce bursts of scroll-driven updates to at most one commit
          // per frame. A pending rAF absorbs all subsequent writes until it
          // fires.
          if (rafRef.current !== null) return;
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            setActive(prev => (prev === l ? prev : l));
          });
        },
        lock: (l: string) => {
          if (!l) return;
          lockedRef.current = l;
          setActive(prev => (prev === l ? prev : l));
        },
        unlock: () => {
          lockedRef.current = null;
        },
      }),
      [],
    );

    useEffect(() => {
      return () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }, []);

    // If the source data changes (search/sort/subcategory) and the current
    // active letter no longer exists in the rail, clear it to avoid a
    // phantom highlight on a letter that isn't rendered.
    useEffect(() => {
      if (active && !letters.includes(active)) {
        setActive('');
        if (lockedRef.current && !letters.includes(lockedRef.current)) {
          lockedRef.current = null;
        }
      }
    }, [letters, active]);

    return (
      <ScrollView
        style={styles.alphaRailOuter}
        contentContainerStyle={styles.alphaRail}
        keyboardShouldPersistTaps="always"
        showsVerticalScrollIndicator={false}>
        {letters.map(L => {
          const isActive = L === active;
          return (
            <TouchableOpacity
              key={L}
              activeOpacity={0.6}
              hitSlop={{ top: 2, bottom: 2, left: 6, right: 6 }}
              onPress={() => onLetterPress(L)}>
              <View
                style={[
                  styles.alphaLetter,
                  isActive && styles.alphaLetterActive,
                ]}>
                <Typography
                  style={[
                    styles.alphaLetterText,
                    isActive && styles.alphaLetterTextActive,
                  ]}>
                  {L}
                </Typography>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    );
  },
);

AlphaRail.displayName = 'AlphaRail';

const createStyles = (theme: any) =>
  StyleSheet.create({
    alphaRailOuter: {
      // width = content width (s(22), room for the circle) + paddingRight
      // to nudge letters away from the screen edge.
      width: s(28),
      paddingRight: s(6),
      flexGrow: 0,
      flexShrink: 0,
      backgroundColor: 'transparent',
    },
    alphaRail: {
      paddingVertical: v(6),
      paddingHorizontal: 0,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    alphaLetter: {
      height: v(22),
      width: s(22),
      marginVertical: v(1),
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: s(11),
      // Transparent border in base state keeps size stable when the active
      // ring is applied (no layout shift on selection).
      borderWidth: borderWidth(1),
      borderColor: 'transparent',
    },
    alphaLetterActive: {
      borderColor: theme.colors.textGray700,
    },
    alphaLetterText: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.textGray700,
      fontSize: fs(12),
      lineHeight: fs(14),
      textAlign: 'center',
    },
    alphaLetterTextActive: {
      color: theme.colors.textGray700,
      fontFamily: GTFonts.bold,
    },
  });
